/**
 * Runtime environment configuration.
 * Single source: window.__ENV__ from public/config.js (loaded before app in index.html).
 */

export interface RuntimeEnv {
  NODE_ENV: string;
  VITE_API_BASE_URL: string;
  VITE_NODE_BASE_URL: string;
  VITE_SOCKET_URL: string;
  VITE_APP_NAME: string;
  VITE_PLATFORM_NAME?: string;
  VITE_GOOGLE_CLIENT_ID: string;
  VITE_GOOGLE_DRIVE_CLIENT_ID: string;
  VITE_GOOGLE_CALENDAR_CLIENT_ID: string;
  VITE_OUTLOOK_CALENDAR_CLIENT_ID: string;
  VITE_OFFICE365_CLIENT_ID: string;
  VITE_SHAREPOINT_CLIENT_ID: string;
  VITE_JIRA_CLIENT_ID: string;
  VITE_SERVICENOW_CLIENT_ID: string;
  VITE_SALESFORCE_CLIENT_ID: string;
  VITE_ZOHO_CRM_CLIENT_ID: string;
  VITE_ZENDESK_CLIENT_ID: string;
  VITE_DISABLE_SUBSCRIPTION?: string;
  /** When true, use file_url for email attachment download instead of presigned_url (e.g. local storage) */
  VITE_DOWNLOAD_FILE?: string;
  /** 'gmail' | 'outlook' | '' - when set, limits visible email connection and apps to that type */
  VITE_MARKETPLACE_IMAGE_TYPE?: string;
  /** Comma-separated app keys hidden from marketplace UI (e.g. 'sharepoint'). Empty string shows all. */
  VITE_HIDDEN_APP_KEYS?: string;
  /** Channel email for which the Prism7 Trusted Advisor / ACL Access Control dashboards are shown. Temporary, channel-gated feature. */
  VITE_PRISM7_DASHBOARD_CHANNEL_EMAIL?: string;
  /** Comma-separated messaging provider keys to hide from the Configure Messaging Channel dialog (e.g. 'whatsapp,slack,google_chat,teams'). Empty string shows all. */
  VITE_HIDDEN_MESSAGING_PROVIDERS?: string;
  /** OAuth client ID for the Google Chat Inbox (DM-only, real-mailbox) provider. Falls back to VITE_GOOGLE_CLIENT_ID when unset. */
  VITE_GCHAT_INBOX_CLIENT_ID?: string;
  /** Visibility switch for the legacy Channel Settings "Channel Workflow" tab. 'true'/'1' (default) shows it; 'false'/'0' hides it. */
  VITE_SHOW_WORKFLOW_TAB?: string;
  /** Shows the "Improve my rules" button in the Edit Channel dialog. 'true'/'1' shows it; anything else (default) hides it.
   * Turn on only together with the backend's BUSINESS_RULE_GEN_V2 and BUSINESS_RULE_GEN_CONTEXT flags. */
  VITE_ENABLE_IMPROVE_RULES?: string;
}

declare global {
  interface Window {
    __ENV__?: RuntimeEnv;
  }
}

/** Defaults when window.__ENV__ is not available (e.g. SSR or before config.js loads) */
const getDefaultEnv = (): RuntimeEnv => ({
  NODE_ENV: typeof import.meta !== 'undefined' && import.meta.env?.MODE ? import.meta.env.MODE : 'production',
  VITE_API_BASE_URL: '',
  VITE_NODE_BASE_URL: 'http://localhost:3001',
  VITE_SOCKET_URL: '',
  VITE_APP_NAME: 'Prism 7',
  VITE_PLATFORM_NAME: 'prism7',
  VITE_GOOGLE_CLIENT_ID: '',
  VITE_OFFICE365_CLIENT_ID: '',
  VITE_GOOGLE_DRIVE_CLIENT_ID: '',
  VITE_GOOGLE_CALENDAR_CLIENT_ID: '',
  VITE_OUTLOOK_CALENDAR_CLIENT_ID: '',
  VITE_SHAREPOINT_CLIENT_ID: '',
  VITE_JIRA_CLIENT_ID: '',
  VITE_SERVICENOW_CLIENT_ID: '',
  VITE_SALESFORCE_CLIENT_ID: '',
  VITE_ZOHO_CRM_CLIENT_ID: '',
  VITE_ZENDESK_CLIENT_ID: '',
  VITE_DISABLE_SUBSCRIPTION: 'false',
  VITE_DOWNLOAD_FILE: 'false',
  VITE_MARKETPLACE_IMAGE_TYPE: '',
  VITE_HIDDEN_APP_KEYS: '',
  VITE_PRISM7_DASHBOARD_CHANNEL_EMAIL: 'shay@giggso.com,demo.hrrecruitment@giggso.com',
  VITE_HIDDEN_MESSAGING_PROVIDERS: '',
  VITE_SHOW_WORKFLOW_TAB: 'true',
  VITE_ENABLE_IMPROVE_RULES: 'false',
});

/**
 * Returns true only in DEV builds where VITE_DISABLE_ADMIN_UI=true is set at build
 * time (e.g. in .env.local). Uses import.meta.env — a build-time constant that Vite
 * statically inlines — so it cannot be overridden from the browser console at runtime.
 * In PROD builds Vite dead-code-eliminates any branch gated on this returning true.
 * To disable the admin_UI gate locally: add VITE_DISABLE_ADMIN_UI=true to .env.local.
 */
export const isAdminUIDisabled = (): boolean => {
  return import.meta.env.VITE_DISABLE_ADMIN_UI === 'true';
};

/**
 * Get runtime environment variables from config.js (window.__ENV__).
 * Fallback to defaults only when __ENV__ is not set (e.g. SSR).
 */
export const getRuntimeEnv = (): RuntimeEnv => {
  if (typeof window === 'undefined' || !window.__ENV__) {
    return getDefaultEnv();
  }
  // config.js is the single source; fill missing keys with defaults
  return { ...getDefaultEnv(), ...window.__ENV__ };
};

/**
 * True when the "Improve my rules" button may be shown (VITE_ENABLE_IMPROVE_RULES is 'true' or '1').
 * Off by default: the button needs the backend's BUSINESS_RULE_GEN_V2 + BUSINESS_RULE_GEN_CONTEXT
 * flags, so it is switched on at deploy time (public/config.js) together with them. Read at call
 * time so a config change or a test can flip it without a rebuild.
 */
export const isImproveRulesEnabled = (): boolean => {
  const value = (getRuntimeEnv().VITE_ENABLE_IMPROVE_RULES ?? 'false').toLowerCase();
  return value === 'true' || value === '1';
};
